import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Settings2,
  Key,
  RefreshCw,
  Loader2,
  Save,
  Info,
} from "lucide-react";
import {
  useEncryptionPolicy,
  useUpsertEncryptionPolicy,
  EncryptionAlgorithm,
  EnforcementMode,
  ALGORITHM_LABELS,
  ROTATION_PRESETS,
  ENFORCEMENT_MODE_LABELS,
  ENFORCEMENT_MODE_DESCRIPTIONS,
} from "@/hooks/useEncryptionPolicies";

const ALL_ALGORITHMS: EncryptionAlgorithm[] = [
  "AES-256-GCM",
  "AES-256-CBC",
  "AES-128-GCM",
  "ChaCha20-Poly1305",
];

interface EncryptionPoliciesFormProps {
  tenantId?: string | null;
}

export function EncryptionPoliciesForm({ tenantId = null }: EncryptionPoliciesFormProps) {
  const { data: policy, isLoading, refetch } = useEncryptionPolicy(tenantId);
  const upsertMutation = useUpsertEncryptionPolicy();

  // Show loading while fetching initial policy data
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Render the actual form with policy data (or defaults if no policy exists)
  return (
    <EncryptionPoliciesFormInner
      key={policy?.id ?? "new"}
      policy={policy}
      tenantId={tenantId}
      upsertMutation={upsertMutation}
      refetch={refetch}
    />
  );
}

interface EncryptionPoliciesFormInnerProps {
  policy: ReturnType<typeof useEncryptionPolicy>["data"];
  tenantId: string | null;
  upsertMutation: ReturnType<typeof useUpsertEncryptionPolicy>;
  refetch: () => void;
}

function EncryptionPoliciesFormInner({
  policy,
  tenantId,
  upsertMutation,
  refetch,
}: EncryptionPoliciesFormInnerProps) {
  // Form state initialized from policy data
  const [approvedAlgorithms, setApprovedAlgorithms] = useState<EncryptionAlgorithm[]>(
    policy?.approved_algorithms ?? ["AES-256-GCM"]
  );
  const [defaultAlgorithm, setDefaultAlgorithm] = useState<EncryptionAlgorithm>(
    policy?.default_algorithm ?? "AES-256-GCM"
  );
  const [minKeyLength, setMinKeyLength] = useState(policy?.min_key_length_bits ?? 256);
  const [maxKeyLength, setMaxKeyLength] = useState(policy?.max_key_length_bits ?? 4096);
  const [requireStrongEntropy, setRequireStrongEntropy] = useState(
    policy?.require_strong_entropy ?? true
  );
  const [rotationPeriodDays, setRotationPeriodDays] = useState(
    policy?.rotation_period_days ?? 90
  );
  const [rotationWarningDays, setRotationWarningDays] = useState(
    policy?.rotation_warning_days ?? 14
  );
  const [enforceRotation, setEnforceRotation] = useState(policy?.enforce_rotation ?? false);
  const [enforcementMode, setEnforcementMode] = useState<EnforcementMode>(
    policy?.enforcement_mode ?? "warn"
  );
  const [autoRotate, setAutoRotate] = useState(policy?.auto_rotate ?? false);
  const [keyDerivationFunction, setKeyDerivationFunction] = useState(
    policy?.key_derivation_function ?? "PBKDF2-SHA256"
  );
  const [keyDerivationIterations, setKeyDerivationIterations] = useState(
    policy?.key_derivation_iterations ?? 100000
  );

  const toggleAlgorithm = (algo: EncryptionAlgorithm) => {
    setApprovedAlgorithms((prev) => {
      if (prev.includes(algo)) {
        // Don't allow removing the last algorithm
        if (prev.length === 1) return prev;
        // If removing the default, set a new default
        const newList = prev.filter((a) => a !== algo);
        if (algo === defaultAlgorithm && newList.length > 0) {
          setDefaultAlgorithm(newList[0]);
        }
        return newList;
      }
      return [...prev, algo];
    });
  };

  const handleSave = async () => {
    if (!approvedAlgorithms.includes(defaultAlgorithm)) {
      toast.error("Default algorithm must be in the approved list");
      return;
    }

    if (minKeyLength > maxKeyLength) {
      toast.error("Minimum key length cannot exceed maximum");
      return;
    }

    if (rotationWarningDays >= rotationPeriodDays) {
      toast.error("Warning days must be less than rotation period");
      return;
    }

    try {
      await upsertMutation.mutateAsync({
        tenant_id: tenantId,
        approved_algorithms: approvedAlgorithms,
        default_algorithm: defaultAlgorithm,
        min_key_length_bits: minKeyLength,
        max_key_length_bits: maxKeyLength,
        require_strong_entropy: requireStrongEntropy,
        rotation_period_days: rotationPeriodDays,
        rotation_warning_days: rotationWarningDays,
        enforce_rotation: enforceRotation,
        enforcement_mode: enforcementMode,
        auto_rotate: autoRotate,
        key_derivation_function: keyDerivationFunction,
        key_derivation_iterations: keyDerivationIterations,
      });
      toast.success("Encryption policy saved successfully");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save policy");
    }
  };

  return (
    <div className="space-y-6">
      {/* Approved Algorithms */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Approved Algorithms
          </CardTitle>
          <CardDescription>
            Select which encryption algorithms are allowed for use
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {ALL_ALGORITHMS.map((algo) => (
              <div key={algo} className="flex items-center space-x-3">
                <Checkbox
                  id={`algo-${algo}`}
                  checked={approvedAlgorithms.includes(algo)}
                  onCheckedChange={() => toggleAlgorithm(algo)}
                />
                <Label htmlFor={`algo-${algo}`} className="flex-1 cursor-pointer">
                  {ALGORITHM_LABELS[algo]}
                </Label>
                {algo === defaultAlgorithm && (
                  <Badge variant="secondary" className="text-xs">
                    Default
                  </Badge>
                )}
              </div>
            ))}
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Default Algorithm</Label>
            <Select value={defaultAlgorithm} onValueChange={(v) => setDefaultAlgorithm(v as EncryptionAlgorithm)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {approvedAlgorithms.map((algo) => (
                  <SelectItem key={algo} value={algo}>
                    {ALGORITHM_LABELS[algo]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              The default algorithm used when creating new encryption keys
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Key Length Requirements */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Key Length Requirements
          </CardTitle>
          <CardDescription>
            Set minimum and maximum key length requirements
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="minKeyLength">Minimum Key Length (bits)</Label>
              <Select
                value={minKeyLength.toString()}
                onValueChange={(v) => setMinKeyLength(parseInt(v))}
              >
                <SelectTrigger id="minKeyLength">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="128">128 bits</SelectItem>
                  <SelectItem value="192">192 bits</SelectItem>
                  <SelectItem value="256">256 bits (Recommended)</SelectItem>
                  <SelectItem value="384">384 bits</SelectItem>
                  <SelectItem value="512">512 bits</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxKeyLength">Maximum Key Length (bits)</Label>
              <Select
                value={maxKeyLength.toString()}
                onValueChange={(v) => setMaxKeyLength(parseInt(v))}
              >
                <SelectTrigger id="maxKeyLength">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="256">256 bits</SelectItem>
                  <SelectItem value="512">512 bits</SelectItem>
                  <SelectItem value="1024">1024 bits</SelectItem>
                  <SelectItem value="2048">2048 bits</SelectItem>
                  <SelectItem value="4096">4096 bits (Recommended)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Require Strong Entropy</Label>
              <p className="text-sm text-muted-foreground">
                Enforce cryptographically secure random number generation
              </p>
            </div>
            <Switch
              checked={requireStrongEntropy}
              onCheckedChange={setRequireStrongEntropy}
            />
          </div>
        </CardContent>
      </Card>

      {/* Key Rotation Policy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Key Rotation Policy
          </CardTitle>
          <CardDescription>
            Configure automatic key rotation schedule and enforcement
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="rotationPeriod">Rotation Period</Label>
              <Select
                value={rotationPeriodDays.toString()}
                onValueChange={(v) => setRotationPeriodDays(parseInt(v))}
              >
                <SelectTrigger id="rotationPeriod">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROTATION_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value.toString()}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                How often encryption keys should be rotated
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="warningDays">Warning Before Expiry</Label>
              <Input
                id="warningDays"
                type="number"
                min={1}
                max={rotationPeriodDays - 1}
                value={rotationWarningDays}
                onChange={(e) => setRotationWarningDays(parseInt(e.target.value) || 14)}
              />
              <p className="text-sm text-muted-foreground">
                Days before rotation to show warnings
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label>Enforce Rotation</Label>
                <p className="text-sm text-muted-foreground">
                  Take action when key rotation is overdue
                </p>
              </div>
              <Switch checked={enforceRotation} onCheckedChange={setEnforceRotation} />
            </div>

            {enforceRotation && (
              <div className="rounded-lg border p-4 space-y-3">
                <Label>Enforcement Mode</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Choose how to handle encryption operations when key rotation is overdue
                </p>
                <div className="space-y-2">
                  {(["warn", "block", "strict"] as const).map((mode) => (
                    <div
                      key={mode}
                      className={`flex items-start space-x-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                        enforcementMode === mode
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      }`}
                      onClick={() => setEnforcementMode(mode)}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <div
                          className={`h-4 w-4 rounded-full border-2 ${
                            enforcementMode === mode
                              ? "border-primary bg-primary"
                              : "border-muted-foreground"
                          }`}
                        >
                          {enforcementMode === mode && (
                            <div className="h-full w-full flex items-center justify-center">
                              <div className="h-1.5 w-1.5 rounded-full bg-white" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{ENFORCEMENT_MODE_LABELS[mode]}</span>
                          {mode === "strict" && (
                            <Badge variant="destructive" className="text-xs">
                              Critical
                            </Badge>
                          )}
                          {mode === "block" && (
                            <Badge variant="secondary" className="text-xs">
                              Recommended
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {ENFORCEMENT_MODE_DESCRIPTIONS[mode]}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label>Auto-Rotate Keys</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically rotate keys when the rotation period expires
                </p>
              </div>
              <Switch checked={autoRotate} onCheckedChange={setAutoRotate} />
            </div>
          </div>

          {enforceRotation && enforcementMode === "strict" && (
            <Alert variant="destructive">
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>Strict mode enabled:</strong> All encryption operations will be blocked and a critical
                alert will be logged when key rotation is overdue. Ensure you have proper key rotation procedures
                and monitoring in place.
              </AlertDescription>
            </Alert>
          )}

          {enforceRotation && enforcementMode === "block" && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                When enforcement is enabled with block mode, encryption operations will fail if the key has not been
                rotated within the specified period. Ensure you have proper key rotation procedures in place.
              </AlertDescription>
            </Alert>
          )}

          {enforceRotation && enforcementMode === "warn" && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Warn mode will log warnings when key rotation is overdue but will still allow encryption operations
                to continue. This is useful for monitoring without disrupting operations.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Key Derivation Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Key Derivation Settings</CardTitle>
          <CardDescription>
            Configure key derivation function parameters
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="kdf">Key Derivation Function</Label>
              <Select value={keyDerivationFunction} onValueChange={setKeyDerivationFunction}>
                <SelectTrigger id="kdf">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PBKDF2-SHA256">PBKDF2-SHA256</SelectItem>
                  <SelectItem value="PBKDF2-SHA512">PBKDF2-SHA512</SelectItem>
                  <SelectItem value="scrypt">scrypt</SelectItem>
                  <SelectItem value="Argon2id">Argon2id (Recommended)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="iterations">Iterations</Label>
              <Select
                value={keyDerivationIterations.toString()}
                onValueChange={(v) => setKeyDerivationIterations(parseInt(v))}
              >
                <SelectTrigger id="iterations">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10000">10,000</SelectItem>
                  <SelectItem value="50000">50,000</SelectItem>
                  <SelectItem value="100000">100,000 (Recommended)</SelectItem>
                  <SelectItem value="200000">200,000</SelectItem>
                  <SelectItem value="500000">500,000</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Reset
        </Button>
        <Button onClick={handleSave} disabled={upsertMutation.isPending}>
          {upsertMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Policy
        </Button>
      </div>
    </div>
  );
}
